<script setup lang="ts">
import { ref, type PropType, computed } from 'vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseTabItem from '@/components/ui/baseTab/BaseTabItem.vue'
import BaseTabs from '@/components/ui/baseTab/BaseTabs.vue'
import FlowStepsSection from '@/components/ui/FlowStepsSection.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

type TabId = 'parcel_forwarding' | 'proxy_shopping'
interface CombinedFlowContent {
	sectionTitle: string
	buttonText: string
	tabs: {
		id: TabId
		title: string
		steps: { title: string; description: string }[]
	}[]
}

const props = defineProps({
	isLoggedIn: {
		type: Boolean,
		required: true,
	},
	content: {
		type: Object as PropType<CombinedFlowContent>,
		required: true,
	},
})

const activeTab = ref(0)
const tabs = computed(() => props.content.tabs)
const currentTab = computed(() => tabs.value[activeTab.value])

const FLOW_ICONS: Record<TabId, string[]> = {
	parcel_forwarding: [
		'flow_warehouse',
		'flow_tracking_number',
		'flow_consolidate',
		'flow_delivery',
	],
	proxy_shopping: [
		'flow_place_order',
		'flow_payment',
		'flow_consolidate',
		'flow_delivery',
	],
}
const TAB_BUTTON_NAMES: Record<TabId, string> = {
	parcel_forwarding: 'Parcel Forwarding Flow Tab',
	proxy_shopping: 'Proxy Shopping Flow Tab',
} as const

const combinedSteps = computed(() => {
	if (!currentTab.value) return []
	return currentTab.value.steps.map((step, index) => ({
		...step,
		icon: FLOW_ICONS[currentTab.value.id][index],
	}))
})

const handleTabClick = (index: number) => {
	activeTab.value = index

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.HomePage,
		button_name: TAB_BUTTON_NAMES[currentTab.value.id],
	})
}

const openRegister = () => {
	window.BsHeaderVm.showSignupPanel()

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.HomePage,
		button_name: 'Sign Up - Flow',
	})
}
</script>

<template>
	<div class="bs-container py-distant-acquaintances">
		<div class="col-span-12 flex flex-col items-center">
			<div
				class="typography-h2-str mb-acquaintances text-center [&>span]:text-o-500"
				v-html="content.sectionTitle"
			></div>
			<BaseTabs
				v-if="tabs && tabs.length > 1"
				size="large"
				type="secondary"
				class="mb-48px"
			>
				<BaseTabItem
					v-for="(tab, index) in tabs"
					:key="index"
					type="secondary"
					size="large"
					:isSelected="index === activeTab"
					@click="handleTabClick(index)"
				>
					{{ tab.title }}
				</BaseTabItem>
			</BaseTabs>

			<template v-if="tabs.length > 0">
				<FlowStepsSection :steps="combinedSteps" />

				<BaseButton
					v-if="!isLoggedIn"
					size="large"
					class="mx-auto mt-acquaintances flex"
					@click="openRegister"
				>
					{{ content.buttonText }}
				</BaseButton>
			</template>
		</div>
	</div>
</template>
